import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MdArrowRight } from "react-icons/md";
import {
  Box,
  Center,
  HStack,
  Heading,
  Image,
  Pressable,
  Spinner,
  Text,
  VStack,
} from "native-base";
import { Helmet } from "react-helmet-async";
import { GiSteeringWheel } from "react-icons/gi";
import moment from "moment";
import styles from "./styles.module.css";
import { StateContext, StatusContext } from "../../../../../contexts";
import colors from "../../../../../constants/colors";
import API, { getFileUrl } from "../../../../../api";
import Seat from "../../../../../components/seat";
import Cell from "../../../../../components/cell";
import { formatToCurrency } from "../../../../../utils";

function AgencyPaymentManagementServicesServicePage() {
  const params = useParams();
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const [isLoading, setIsLoading] = useState(true);
  const [service, setService] = useState({});
  const [name, setName] = useState("");
  const [responsible, setResponsible] = useState("");

  const getData = useCallback(async () => {
    try {
      setIsLoading(true);
      const {
        data: {
          data: { name: _name, responsible: _responsible, services },
        },
      } = await API(state.jwt).get(`/tours/${params.id}`);
      setService(
        services.find((_service) => _service.id === Number(params.service)),
      );
      setName(_name);
      setResponsible(_responsible);
    } catch (error) {
      status.updateErrorMessage(error);
    }
    setIsLoading(false);
  }, [params.id, params.service, state.jwt, status]);

  useEffect(() => {
    getData();
  }, [getData]);

  const onConfirm = useCallback(
    async (id) => {
      try {
        await API(state.jwt).post(`/bookings/${id}/confirm`);
        await getData();
      } catch (error) {
        status.updateErrorMessage(error);
      }
    },
    [getData, state.jwt, status],
  );

  const capacity =
    service?.vehicle?.capacity ||
    service?.quotation?.vehicleType?.capacity ||
    0;

  return (
    <VStack>
      <Helmet>
        <title>Ver servicios</title>
      </Helmet>
      <HStack paddingY={8} space={5}>
        <Heading color={colors.secondary} fontSize="2xl">
          Gestión de cobros
        </Heading>
        <MdArrowRight color={colors.secondary} size={25} />
        <Heading color={colors.secondary} fontSize="2xl">
          Ver servicios
        </Heading>
      </HStack>
      {isLoading ? (
        <Center height={300}>
          <Spinner size="lg" color={colors.primary} />
        </Center>
      ) : (
        <HStack paddingX={6} paddingY={8} alignItems="flex-start" space={5}>
          <VStack maxWidth={230} width="full" space={10}>
            <VStack
              paddingX={4}
              paddingY={6}
              borderColor={colors.secondary}
              borderWidth={2}
              borderRadius={10}
              space={5}>
              <GiSteeringWheel color={colors.gray} size={30} />
              {Array.from(new Array(Math.ceil(capacity / 4))).map((_, i) => (
                <HStack key={String(i)} justifyContent="space-between">
                  {Array.from(new Array(Math.ceil(5))).map((__, j) => {
                    let position = i * 4 + j;
                    if (j <= 1) {
                      position += 1;
                    }
                    return (
                      <Seat
                        key={String(j)}
                        position={position}
                        isEmpty={position > capacity || j === 2}
                        isBlocked={service?.bookings
                          ?.filter((booking) => booking.confirmed)
                          ?.some((booking) => booking.seat === position)}
                        disabled
                      />
                    );
                  })}
                </HStack>
              ))}
            </VStack>
          </VStack>
          <VStack flex={1}>
            <HStack
              backgroundColor={colors.secondary}
              paddingY={3}
              paddingX={5}
              space={20}
              width="fit-content">
              <VStack>
                <Text bold color={colors.white}>
                  Folio
                </Text>
                <Text color={colors.white}>#{service.id}</Text>
              </VStack>
              <VStack>
                <Text bold color={colors.white}>
                  Conductor
                </Text>
                <Text color={colors.white}>
                  {service?.driver
                    ? `${service?.driver.firstName} ${service?.driver.lastName}`
                    : "SIN ASIGNAR"}
                </Text>
              </VStack>
              <VStack>
                <Text bold color={colors.white}>
                  Vehículo
                </Text>
                <Text color={colors.white}>
                  {service?.vehicle ? service?.vehicle.plate : "SIN ASIGNAR"}
                </Text>
              </VStack>
            </HStack>
            <Image
              source={getFileUrl(
                service?.quotation?.vehicleType?.photos.at(0).url,
              )}
              width={400}
              height={240}
              alt="bus"
            />
            <HStack alignItems="start" space={5}>
              <HStack
                alignItems="start"
                backgroundColor={colors.lighterGray}
                space={5}
                padding={2}>
                <VStack>
                  <Text bold>Tipo de unidad</Text>
                  <Text>{service?.quotation?.vehicleType?.name}</Text>
                </VStack>
                <VStack space={2}>
                  <VStack>
                    <Text bold>Asientos disponibles:</Text>
                    <Text>{capacity}</Text>
                  </VStack>
                  <VStack>
                    <Text bold>Asientos ocupados</Text>
                    <Text>{service?.bookings?.length}</Text>
                  </VStack>
                </VStack>
              </HStack>
              <VStack space={2}>
                <HStack alignItems="flex-end">
                  <Text bold>
                    Nombre
                    <br />
                    personalizado:
                  </Text>
                  <Text> {name || "N/A"}</Text>
                </HStack>
                <HStack alignItems="flex-end">
                  <Text bold>
                    Nombre del
                    <br />
                    responsable:
                  </Text>
                  <Text> {responsible || "N/A"}</Text>
                </HStack>
              </VStack>
            </HStack>
            <table className={styles.table}>
              <thead>
                <tr>
                  <Cell header>
                    <Text color={colors.white}>
                      ID DE
                      <br />
                      RESERVA
                    </Text>
                  </Cell>
                  <Cell header color={colors.white}>
                    <Text color={colors.white}>
                      FECHA
                      <br />
                      DE VENTA
                    </Text>
                  </Cell>
                  <Cell header color={colors.white}>
                    <Text color={colors.white}>
                      NOMBRE DEL
                      <br />
                      PASAJERO
                    </Text>
                  </Cell>
                  <Cell header color={colors.white}>
                    <Text color={colors.white}>
                      TIPO DE
                      <br />
                      PASAJERO
                    </Text>
                  </Cell>
                  <Cell header color={colors.white}>
                    ASIENTO
                  </Cell>
                  <Cell header color={colors.white}>
                    PRECIO
                  </Cell>
                  <Cell header color={colors.white}>
                    STATUS
                  </Cell>
                </tr>
              </thead>
              <tbody>
                {service?.bookings?.map((booking) => (
                  <tr key={booking.id}>
                    <Cell>{booking.id}</Cell>
                    <Cell>{moment(booking.createdAt).format("DD/MM/Y")}</Cell>
                    <Cell>
                      {booking.user.firstName} {booking.user.lastName}
                    </Cell>
                    <Cell>{booking.cartItem.name}</Cell>
                    <Cell>{booking.seat}</Cell>
                    <Cell>{formatToCurrency(booking.transaction.amount)}</Cell>
                    <Cell>
                      <Text>
                        {booking.confirmed ? "CONFIRMADA" : "PENDIENTE"}
                      </Text>
                      {!booking.confirmed && (
                        <Pressable onPress={() => onConfirm(booking.id)}>
                          <Box
                            backgroundColor={colors.primary}
                            borderRadius={5}
                            paddingX={3}
                            paddingY={1}>
                            <Text color={colors.white} textAlign="center">
                              Confirmar
                            </Text>
                          </Box>
                        </Pressable>
                      )}
                    </Cell>
                  </tr>
                ))}
              </tbody>
            </table>
          </VStack>
        </HStack>
      )}
    </VStack>
  );
}

export default AgencyPaymentManagementServicesServicePage;
